'use client';

import { useCallback, useRef, useState } from 'react';
import { useTranslations } from 'next-intl';
import { useQuery } from '@tanstack/react-query';
import QRCode from 'react-qr-code';
import {
  ArrowRight,
  Truck,
  Package,
  MapPin,
  Calendar,
  Users,
  FileText,
  AlertCircle,
  Hash,
  Clock,
  Download,
  Box,
  Printer,
  CheckCircle2,
  Send,
  Loader2
} from 'lucide-react';

import { useLocale } from 'next-intl';
import { useRouter } from '@/i18n/routing';
import { formatDate } from '@/lib/format';
import { PrintPortal } from '@/components/print-portal';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { PermissionGate } from '@/features/auth';
import { shippingQueries } from '../services/shipping.queries';
import {
  useConfirmShipmentMutation,
  useDispatchShipmentMutation
} from '../services/shipping.mutations';
import { ShipmentPrintReceipt } from './shipment-print-receipt';
import { ShippingStatusBadge } from './shipping-status-badge';
import { ShippingPriorityTag } from './shipping-priority-tag';
import type { Shipment } from '../types';

/*************  ✨ Windsurf Command ⭐  *************/
/**
 * A component that displays a row of information with an icon, label, and value.
 *
 * @param {React.ComponentType<{ className?: string >} icon - The icon to display.
 * @param {string} label - The label to display.
 * @param {React.ReactNode} value - The value to display.
 * @returns {JSX.Element} A JSX element containing the information row.
 */
/*******  49a383fb-31c9-46b9-bac0-9685119592a6  *******/ // ─── Info row helper ────────────────────────────────────────────────────────

function InfoRow({
  icon: Icon,
  label,
  value
}: {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  value: React.ReactNode;
}) {
  return (
    <div className='space-y-1'>
      <p className='text-muted-foreground text-sm'>{label}</p>
      <div className='flex items-center gap-2'>
        <Icon className='text-muted-foreground h-4 w-4 shrink-0' />
        <p className='font-semibold'>{value ?? '—'}</p>
      </div>
    </div>
  );
}

// ─── Component ──────────────────────────────────────────────────────────────

interface ShippingDetailPageProps {
  shipmentId: number;
}

export function ShippingDetailPage({ shipmentId }: ShippingDetailPageProps) {
  const t = useTranslations('shipping');
  const locale = useLocale();
  const router = useRouter();
  const qrRef = useRef<HTMLDivElement>(null);

  const [printMode, setPrintMode] = useState<'receipt' | 'qr' | null>(null);
  const { data, isLoading } = useQuery(shippingQueries.detail(shipmentId));
  const shipment = data?.data as Shipment | undefined;

  const confirmMutation = useConfirmShipmentMutation();
  const dispatchMutation = useDispatchShipmentMutation();

  const triggerPrint = useCallback((mode: 'receipt' | 'qr') => {
    setPrintMode(mode);
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        window.print();
        setPrintMode(null);
      });
    });
  }, []);

  const handlePrint = useCallback(
    () => triggerPrint('receipt'),
    [triggerPrint]
  );
  const handlePrintQr = useCallback(() => triggerPrint('qr'), [triggerPrint]);

  const handleDownloadQr = useCallback(() => {
    if (!qrRef.current || !shipment) return;
    const svg = qrRef.current.querySelector('svg');
    if (!svg) return;

    const svgData = new XMLSerializer().serializeToString(svg);
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const img = new window.Image();

    img.onload = async () => {
      canvas.width = img.width;
      canvas.height = img.height;
      ctx?.drawImage(img, 0, 0);
      const imgData = canvas.toDataURL('image/png');

      const { jsPDF } = await import('jspdf');
      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4'
      });

      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();
      const qrSize = 80;
      const x = (pdfWidth - qrSize) / 2;
      const y = (pdfHeight - qrSize) / 2 - 10;

      pdf.addImage(imgData, 'PNG', x, y, qrSize, qrSize);
      pdf.setFontSize(14);
      pdf.text(shipment.shipment_number, pdfWidth / 2, y + qrSize + 10, {
        align: 'center'
      });

      pdf.save(`${shipment.shipment_number}-qr.pdf`);
    };

    img.src = `data:image/svg+xml;base64,${btoa(unescape(encodeURIComponent(svgData)))}`;
  }, [shipment]);

  // ── Loading ───────────────────────────────────────────────────────────────
  if (isLoading) {
    return (
      <div className='space-y-6'>
        <div className='flex items-center gap-2'>
          <Skeleton className='h-10 w-10' />
          <Skeleton className='h-8 w-64' />
        </div>
        <div className='grid gap-6 lg:grid-cols-[2fr_1fr]'>
          <Skeleton className='h-80' />
          <Skeleton className='h-80' />
        </div>
      </div>
    );
  }

  // ── Not found ─────────────────────────────────────────────────────────────
  if (!shipment) {
    return (
      <div className='flex flex-col items-center justify-center py-20 text-center'>
        <AlertCircle className='text-muted-foreground mb-4 h-12 w-12' />
        <p className='text-muted-foreground text-lg'>{t('shipmentNotFound')}</p>
        <Button
          variant='outline'
          className='mt-4'
          onClick={() => router.push('/dashboard/shipping')}
        >
          {t('backToList')}
        </Button>
      </div>
    );
  }

  return (
    <div className='space-y-6'>
      {/* Header */}
      <div className='flex items-center gap-3'>
        <Button variant='ghost' size='icon' onClick={() => router.back()}>
          <ArrowRight className='h-4 w-4' />
        </Button>
        <div className='flex flex-1 items-center gap-3'>
          <h1 className='text-2xl font-bold'>#{shipment.shipment_number}</h1>
          <ShippingPriorityTag priority={shipment.priority} />
          <ShippingStatusBadge status={shipment.status} />
        </div>
        {/* Both stage transitions post to `shipments.edit` routes. The screen
            itself is readable by `supervisor.view` too, so the buttons carry
            their own gate rather than inheriting the route's. */}
        {shipment.status === 'draft' && (
          <PermissionGate permissions={['shipments.edit']}>
            <Button
              size='sm'
              onClick={() => confirmMutation.mutate(shipment.id)}
              disabled={
                confirmMutation.isPending ||
                !shipment.driver_id ||
                !shipment.box_count
              }
              title={
                !shipment.driver_id
                  ? t('confirmRequiresDriver')
                  : !shipment.box_count
                    ? t('confirmRequiresBox')
                    : undefined
              }
            >
              {confirmMutation.isPending ? (
                <Loader2 className='me-2 h-4 w-4 animate-spin' />
              ) : (
                <CheckCircle2 className='me-2 h-4 w-4' />
              )}
              {t('confirmAction')}
            </Button>
          </PermissionGate>
        )}
        {shipment.status === 'ready_for_shipping' && (
          <PermissionGate permissions={['shipments.edit']}>
            <Button
              size='sm'
              onClick={() => dispatchMutation.mutate(shipment.id)}
              disabled={dispatchMutation.isPending}
            >
              {dispatchMutation.isPending ? (
                <Loader2 className='me-2 h-4 w-4 animate-spin' />
              ) : (
                <Send className='me-2 h-4 w-4' />
              )}
              {t('dispatchAction')}
            </Button>
          </PermissionGate>
        )}
        {shipment.boxes?.some((box) => box.registrations?.length) && (
          <Button variant='outline' size='sm' onClick={handlePrint}>
            <Printer className='me-2 h-4 w-4' />
            {t('printForm')}
          </Button>
        )}
      </div>

      {/* Main layout */}
      <div className='grid gap-6 lg:grid-cols-[2fr_1fr]'>
        {/* Left column — details */}
        <div className='flex flex-col gap-6'>
          {/* Shipment info */}
          <Card>
            <CardHeader>
              <CardTitle className='flex items-center gap-2'>
                <Truck className='h-5 w-5' />
                {t('detailShipmentInfo')}
              </CardTitle>
            </CardHeader>
            <CardContent className='grid gap-4 sm:grid-cols-2'>
              <InfoRow
                icon={Hash}
                label={t('shipmentNumber')}
                value={`#${shipment.shipment_number}`}
              />
              <InfoRow
                icon={Calendar}
                label={t('shipmentDate')}
                value={formatDate(
                  shipment.shipment_date ?? shipment.created_at,
                  {},
                  locale
                )}
              />
              <InfoRow
                icon={MapPin}
                label={t('location')}
                value={shipment.location}
              />
              <InfoRow
                icon={Package}
                label={t('boxCount')}
                value={shipment.box_count}
              />
              <InfoRow
                icon={Users}
                label={t('driverColumn')}
                value={shipment.driver?.name}
              />
              <InfoRow
                icon={Users}
                label={t('assignedEmployee')}
                value={shipment.assigned_employee}
              />
            </CardContent>
          </Card>

          {/* Notes */}
          {shipment.notes && (
            <Card>
              <CardHeader>
                <CardTitle className='flex items-center gap-2'>
                  <FileText className='h-5 w-5' />
                  {t('notes')}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className='text-muted-foreground text-sm whitespace-pre-wrap'>
                  {shipment.notes}
                </p>
              </CardContent>
            </Card>
          )}

          {/* Boxes */}
          {shipment.boxes && shipment.boxes.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className='flex items-center gap-2'>
                  <Box className='h-5 w-5' />
                  {t('detailBoxes')} ({shipment.boxes.length})
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className='divide-y'>
                  {shipment.boxes.map((box) => (
                    <div
                      key={box.id}
                      className='flex items-center justify-between py-3 first:pt-0 last:pb-0'
                    >
                      <div className='flex items-center gap-3'>
                        <div className='bg-muted flex h-9 w-9 items-center justify-center rounded-lg'>
                          <Package className='text-muted-foreground h-4 w-4' />
                        </div>
                        <div>
                          <p className='text-sm font-medium'>
                            {box.box_number}
                          </p>
                          {box.box_type && (
                            <p className='text-muted-foreground text-xs'>
                              {box.box_type.name}
                            </p>
                          )}
                        </div>
                      </div>
                      <Badge variant='secondary' className='text-xs'>
                        {t(`boxStatus_${box.status}`)}
                      </Badge>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Right column — sidebar */}
        <div className='flex flex-col gap-6'>
          {/* Status card */}
          <Card>
            <CardHeader>
              <CardTitle>{t('status')}</CardTitle>
            </CardHeader>
            <CardContent className='space-y-4'>
              <div className='flex items-center gap-2'>
                <ShippingStatusBadge status={shipment.status} />
              </div>
              <div className='flex items-center gap-2'>
                <ShippingPriorityTag priority={shipment.priority} />
              </div>
              {shipment.confirmed_at && (
                <div className='flex items-center gap-2 text-sm'>
                  <Clock className='text-muted-foreground h-4 w-4' />
                  <span className='text-muted-foreground'>
                    {t('detailConfirmedAt')}:
                  </span>
                  <span className='font-medium'>
                    {formatDate(
                      shipment.confirmed_at,
                      { hour: 'numeric', minute: 'numeric' },
                      locale
                    )}
                  </span>
                </div>
              )}
            </CardContent>
          </Card>

          {/* QR Code */}
          {shipment.qr_code && (
            <Card>
              <CardHeader className='text-center'>
                <CardTitle>{t('detailQrCode')}</CardTitle>
              </CardHeader>
              <CardContent className='flex flex-col items-center space-y-4'>
                <div ref={qrRef} className='rounded-lg bg-white p-4'>
                  <QRCode value={shipment.qr_code} size={180} level='M' />
                </div>
                <p className='font-mono text-sm font-semibold'>
                  {shipment.qr_code}
                </p>
                <Button
                  variant='outline'
                  className='w-full'
                  onClick={handlePrintQr}
                >
                  <Printer className='me-2 h-4 w-4' />
                  {t('printQr')}
                </Button>
                <Button
                  variant='outline'
                  className='w-full'
                  onClick={handleDownloadQr}
                >
                  <Download className='me-2 h-4 w-4' />
                  {t('downloadQr')}
                </Button>
              </CardContent>
            </Card>
          )}

          {/* Meta */}
          <Card>
            <CardHeader>
              <CardTitle>{t('detailMeta')}</CardTitle>
            </CardHeader>
            <CardContent className='space-y-3 text-sm'>
              {shipment.creator && (
                <div className='flex justify-between'>
                  <span className='text-muted-foreground'>
                    {t('detailCreatedBy')}
                  </span>
                  <span className='font-medium'>{shipment.creator.name}</span>
                </div>
              )}
              <div className='flex justify-between'>
                <span className='text-muted-foreground'>
                  {t('detailCreatedAt')}
                </span>
                <span className='font-medium'>
                  {formatDate(
                    shipment.created_at,
                    { hour: 'numeric', minute: 'numeric' },
                    locale
                  )}
                </span>
              </div>
              <div className='flex justify-between'>
                <span className='text-muted-foreground'>
                  {t('detailUpdatedAt')}
                </span>
                <span className='font-medium'>
                  {formatDate(
                    shipment.updated_at,
                    { hour: 'numeric', minute: 'numeric' },
                    locale
                  )}
                </span>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {shipment && (
        <ShipmentPrintReceipt
          shipment={shipment}
          visible={printMode === 'receipt'}
        />
      )}

      {shipment?.qr_code && printMode === 'qr' && (
        <PrintPortal>
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              minHeight: '100vh',
              padding: '20mm',
              fontFamily: 'monospace'
            }}
          >
            <QRCode value={shipment.qr_code} size={220} level='M' />
            <p
              style={{
                marginTop: '16px',
                fontSize: '20px',
                fontWeight: 'bold',
                letterSpacing: '2px'
              }}
            >
              {shipment.shipment_number}
            </p>
            <p
              style={{
                marginTop: '4px',
                fontSize: '14px'
              }}
            >
              {shipment.qr_code}
            </p>
          </div>
        </PrintPortal>
      )}
    </div>
  );
}
